import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import { articles, categories } from '@/mocks/siteData';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  const article = useMemo(() => articles.find((a) => a.slug === slug), [slug]);
  const relatedArticles = useMemo(() => {
    if (!article) return [];
    return articles
      .filter((a) => a.category === article.category && a.id !== article.id)
      .slice(0, 3);
  }, [article]);

  if (!article) {
    return (
      <div className="pt-24 text-center">
        <p className="text-foreground-600">文章未找到</p>
        <Link to="/" className="text-primary-600 hover:underline mt-2 inline-block">返回首页</Link>
      </div>
    );
  }

  const categoryName = categories.find((c) => c.slug === article.category)?.name || article.category;

  return (
    <>
      <SEOHead
        title={article.title}
        description={article.excerpt}
        keywords={`禁漫天堂,${categoryName},${article.title.slice(0, 20)}`}
        canonicalPath={`/article/${article.slug}`}
        ogType="article"
        schema={[
          {
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.excerpt,
            author: {
              '@type': 'Organization',
              name: article.author,
            },
            datePublished: article.date,
            url: `https://jmtthub.com/article/${article.slug}`,
            publisher: {
              '@type': 'Organization',
              name: '禁漫天堂',
            },
          },
        ]}
      />

      <main className="pt-20">
        <div className="w-full px-4 md:px-6">
          <Breadcrumbs
            items={[
              { label: '首页', href: '/' },
              { label: '分类浏览', href: '/category/hot' },
              { label: categoryName, href: `/category/${article.category}` },
              { label: article.title },
            ]}
          />
        </div>

        <article className="w-full py-8 md:py-12">
          <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
            <div className="mb-8">
              <div className="flex items-center gap-3 text-xs text-foreground-500 mb-4">
                <Link
                  to={`/category/${article.category}`}
                  className="px-3 py-1 rounded-full bg-primary-100 text-primary-700 font-medium hover:bg-primary-200 transition-colors"
                >
                  {categoryName}
                </Link>
                <span>{article.date}</span>
                <span>·</span>
                <span>{article.readTime}阅读</span>
                <span>·</span>
                <span>{article.author}</span>
              </div>
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 leading-tight mb-6">
                {article.title}
              </h1>
              <p className="text-base md:text-lg text-foreground-600 leading-relaxed">
                {article.excerpt}
              </p>
            </div>

            <div className="mb-10 rounded-lg overflow-hidden">
              <img
                src={article.coverImage}
                alt={article.title}
                className="w-full aspect-[16/9] object-cover"
              />
            </div>

            <div
              className="prose prose-lg max-w-none text-foreground-800 leading-relaxed
                [&_h2]:text-xl [&_h2]:md:text-2xl [&_h2]:font-heading [&_h2]:font-bold [&_h2]:text-foreground-900 [&_h2]:mt-10 [&_h2]:mb-4
                [&_h3]:text-lg [&_h3]:md:text-xl [&_h3]:font-heading [&_h3]:font-semibold [&_h3]:text-foreground-900 [&_h3]:mt-8 [&_h3]:mb-3
                [&_p]:mb-4 [&_p]:leading-relaxed
                [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-4 [&_ul]:space-y-1
                [&_li]:text-foreground-700 [&_li]:leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            {relatedArticles.length > 0 && (
              <section className="mt-16 pt-10 border-t border-background-200">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6">
                  相关文章
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {relatedArticles.map((related) => (
                    <Link
                      key={related.id}
                      to={`/article/${related.slug}`}
                      className="group bg-background-100 rounded-lg overflow-hidden transition-all duration-300 hover:bg-background-200/50"
                    >
                      <div className="aspect-[16/9] overflow-hidden">
                        <img
                          src={related.coverImage}
                          alt={related.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                      </div>
                      <div className="p-4">
                        <div className="text-xs text-foreground-500 mb-2">{related.date}</div>
                        <h3 className="text-sm font-heading font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2">
                          {related.title}
                        </h3>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </div>
        </article>
      </main>
    </>
  );
}